import * as a from '@mitranim/js/all.mjs'
import * as uc from './util-coll.mjs'
import * as ucc from './util-coll-cls.mjs'
import * as um from './util-misc.mjs'
import * as ue from './util-err.mjs'

// Superclass for all pages and feeds.
export class PageOrFeed extends a.Struct {
  static spec = {
    // Set internally on the client. Used by `.nextPageParams`.
    params: a.optDict,
  }

  // Previously required for `.nextPageParams`. Might no longer be necessary,
  // because `.nextPageParams` now includes the page inputs returned by the
  // server as the field `.input`. TODO recheck, and if no longer necessary,
  // drop this method and the calls to it.
  setParams(val) {this.params = a.reqDict(val)}
  getParams() {return this.params}

  // TODO rename to `.getLen` for consistency with other methods.
  len() {return a.len(this.getItems())}
  hasItems() {return a.hasLen(this.getItems())}

  // Override in subclass.
  getItems() {}
  getOffset() {}
  hasPrev() {return this.getOffset() > 0}
  hasNext() {}
  getNextOffset() {return a.laxNat(this.getOffset()) + a.laxNat(this.len())}

  // Override in subclass.
  // More or less deprecated.
  // The server doesn't always return totals.
  // TODO rip out.
  getTotal() {}

  // Override in subclass. This method must always return full entities.
  getEnts() {return this.getItems()}

  nextPageParams() {
    // All page endpoints accept the `.offset` field and handle it
    // consistently.
    return {
      // The field `.input` is provided by `cs-core` and contains a snapshot
      // of the request parameters.
      ...a.optDict(this.input),
      ...a.optDict(this.params),
      offset: this.getNextOffset(),
    }
  }
}

// Superclass for both `ClsPage` and `BukPage`.
export class FeedPage extends PageOrFeed {
  static spec = {items: a.laxArr}
  getItems() {return this.items}
}

class PageInfo extends a.Struct {
  static spec = {
    total: a.laxNat,
    totalPages: a.laxNat,
    page: a.laxNat,
    perPage: a.laxNat,
    nextPage: a.optNat,
    prevPage: a.optNat,
    hasNext: a.laxBool,
    hasPrev: a.laxBool,
  }

  getOffset() {return a.laxNat(this.page) * a.laxNat(this.perPage)}
}

// Used for non-buket API responses.
export class ClsPage extends FeedPage {
  static spec = {
    items(val) {return a.toInst(val, this.cls)},
    pageInfo(val) {return a.toInstOpt(val, PageInfo)},
  }

  get cls() {return a.Vec}
  static get cls() {return this.prototype.cls}

  hasPrev() {return this.pageInfo?.hasPrev}
  hasNext() {return this.pageInfo?.hasNext}
  getOffset() {return this.pageInfo?.getOffset()}
  getTotal() {return this.pageInfo?.total}
}

// Used for buket API responses.
export class BukPage extends um.MixBuketed(FeedPage) {
  static spec = {
    key: a.optStr,
    items: a.optArr,
    input: a.optStruct,
    offset: a.optNat,
    limit: a.optNat,
    total: a.optNat,
    more: a.optBool,
  }

  pk() {return this.key}
  hasNext() {return this.more}
  getOffset() {return this.offset}
  getTotal() {return this.total}
  getEnts() {return this.coll()?.fromItems(this.getItems())}
  reverse() {return this.items = a.reverse(this.items), this}

  // Override in subclass. Must return an instance of `MutColl`.
  // Enables `.getEnts`, which enables compatibility with view code
  // that expects "extended" entities in the style of non-buket API.
  coll() {throw ue.errImplementInSubclass()}
}

export class BukPages extends ucc.ModelColl {get cls() {return BukPage}}

/*
Feed with multiple pages, which must be `ClsPage`.
Deprecated, use the buket API for newer code.
*/
export class ClsFeed extends PageOrFeed {
  static spec = {
    params: a.reqDict,
    pages: a.optArr,
  }

  get cls() {return ClsPage}

  len() {return uc.sumBy(this.pages, pageLen)}
  getOffset() {return this.lastPage()?.getOffset()}
  getItems() {return a.mapFlat(this.pages, pageItems)}
  hasItems() {return a.some(this.pages, pageLen)}
  getTotal() {return this.lastPage()?.getTotal()}
  firstPage() {return a.head(this.pages)}
  lastPage() {return a.last(this.pages)}
  hasNext() {return this.lastPage()?.hasNext()}
  hasPage() {return a.hasLen(this.pages)}

  withFirstPage(page) {
    const params = this.params
    const pages = page && [a.toInst(page, this.cls)]
    return new this.constructor({params, pages})
  }

  withNextPage(page) {
    const params = this.params
    const pages = a.append(this.pages, a.toInst(page, this.cls))
    return new this.constructor({params, pages})
  }

  truncatedToFirstPage() {
    return this.withFirstPage(this.firstPage())
  }
}

export function isPageOrFeed(val) {return a.isInst(val, PageOrFeed)}
export function isFeed(val) {return a.isInst(val, ClsFeed)}
export function pageHasNext(val) {return a.optStruct(val)?.hasNext()}
export function pageHasItems(val) {return a.optStruct(val)?.hasItems()}
export function pageItems(val) {return a.arr(a.optStruct(val)?.getItems())}
export function pageEnts(val) {return a.arr(a.optStruct(val)?.getEnts())}
export function pageLen(val) {return a.optStruct(val)?.len()}
export function pageObsEnts(val) {return pageEnts(um.getValue(val))}

// Deprecated. The server no longer returns totals. Once we "stabilize" on the
// decision to avoid totals, this should be ripped out of client code.
export function pageTotal(val) {return a.optStruct(val).getTotal()}
export function pageObsTotal(val) {return pageTotal(um.getValue(val))}
